import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import StateForm from '../../components/Forms/StateForm';
import ConfirmDialog from '../../components/ConfirmDialog';
import LogsAction from '../../components/LogsAction';
import statesApi from '../../api/masters/statesApi';
import { getSidebarState, saveSidebarState, saveScrollPosition, getScrollPosition } from '../../utils/stateManager';
import { hasPermission, PERMISSIONS } from '../../utils/permissions';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import './StatesPage.css';

const DEFAULTS = { english_name: 150, hindi_name: 150, sequence: 80, status: 80, actions: 90 };

export default function States() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirm, setConfirm] = useState({ open: false, id: null, title: '', message: '' });
  const [message, setMessage] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const canViewMasters = hasPermission(PERMISSIONS.MASTERS_VIEW);
  const canManageMasters = hasPermission(PERMISSIONS.MASTERS_MANAGE);
  const canDeleteMasters = hasPermission(PERMISSIONS.MASTERS_DELETE);
  const navigate = useNavigate();
  const { colWidths, rHandle } = useResizableColumns('states-col-widths', DEFAULTS);

  useEffect(() => {
    // Restore sidebar state
    setSidebarOpen(getSidebarState());
    if (canViewMasters) {
      fetchStates();
    } else {
      setLoading(false);
    }
  }, [canViewMasters]);

  useEffect(() => {
    // Restore scroll position when not in form mode
    if (!showForm) {
      const mainContent = document.querySelector('.main-content');
      if (mainContent) {
        const scrollPos = getScrollPosition('states-scroll');
        mainContent.scrollTop = scrollPos;

        // Save scroll position on scroll
        const handleScroll = () => {
          saveScrollPosition('states-scroll', mainContent.scrollTop);
        };
        mainContent.addEventListener('scroll', handleScroll);
        return () => mainContent.removeEventListener('scroll', handleScroll);
      }
    }
  }, [showForm]);

  const fetchStates = async () => {
    setLoading(true);
    try {
      const response = await statesApi.getAll();
      // Sort by sequence
      const sorted = (response.data.data || []).sort((a, b) => {
        const seqA = a.sequence || 0;
        const seqB = b.sequence || 0;
        return seqA - seqB;
      });
      setStates(sorted);
    } catch (error) {
      console.error('Error fetching states:', error);
      setMessage({ type: 'error', text: 'Failed to fetch states' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
    navigate('/login');
  };

  const handleMenuClick = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    saveSidebarState(newState);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    if (!canManageMasters) return;
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    if (!canManageMasters) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, dropIndex) => {
    if (!canManageMasters) return;
    e.preventDefault();
    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      return;
    }

    try {
      const newStates = [...states];
      const draggedState = newStates[draggedItem];
      newStates.splice(draggedItem, 1);
      newStates.splice(dropIndex, 0, draggedState);

      // Update sequence numbers
      const updatedStates = newStates.map((state, idx) => ({
        ...state,
        sequence: idx + 1,
      }));

      setStates(updatedStates);
      setDraggedItem(null);

      // Call API to update sequences
      await statesApi.updateSequence(updatedStates);
      setMessage({ type: 'success', text: 'Sequence updated successfully' });
    } catch (error) {
      console.error('Error updating sequence:', error);
      setMessage({ type: 'error', text: 'Failed to update sequence' });
      fetchStates(); // Revert on error
    }
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  // open confirm dialog
  const confirmDelete = (id) => {
    setConfirm({
      open: true,
      id,
      title: 'Delete State',
      message: 'Are you sure you want to delete this state? This action cannot be undone.',
    });
  };

  const handleDeleteConfirmed = async () => {
    const id = confirm.id;
    setConfirm({ ...confirm, open: false });
    try {
      await statesApi.delete(id);
      setStates(states.filter((s) => s.id !== id));
      setMessage({ type: 'success', text: 'State deleted successfully' });
    } catch (error) {
      console.error('Error deleting state:', error);
      setMessage({ type: 'error', text: 'Failed to delete state' });
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingId(null);
    fetchStates();
  };

  const handleEdit = (state) => {
    setEditingId(state.id);
    setShowForm(true);
  };

  return (
    <div className="dashboard-container">
      <Header onMenuClick={handleMenuClick} onLogout={handleLogout} />
      <div className="dashboard-content">
        <Sidebar isOpen={sidebarOpen} />
        <main className={`main-content states-page ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
          <div className="content-wrapper">
            {message && (
              <div className={`inline-message ${message.type === 'error' ? 'error' : 'success'}`}>
                {message.text}
                <button className="msg-close" onClick={() => setMessage(null)}>✕</button>
              </div>
            )}

            {!canViewMasters ? (
              <div className="inline-message error">You do not have permission to view masters.</div>
            ) : !showForm ? (
              <>
                <div className="list-header">
  <h1>States Management</h1>
  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {canManageMasters && (
                    <button
                      className="btn-primary small"
                      onClick={() => {
                        setEditingId(null);
                        setShowForm(true);
                      }}
                    >
                      + Add State
                    </button>
                  )}
    {canViewMasters && (
      <LogsAction
        category="state"
        title="State Logs"
      />
    )}
  </div>
</div>

                <div className="table-container">
                  <table className="data-table draggable col-resizable" style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th className="drag-handle"></th>
                        <th style={{ width: colWidths.english_name }}>English Name{rHandle('english_name')}</th>
                        <th style={{ width: colWidths.hindi_name }}>Hindi Name{rHandle('hindi_name')}</th>
                        <th style={{ width: colWidths.sequence }}>Sequence{rHandle('sequence')}</th>
                        <th style={{ width: colWidths.status }}>Status{rHandle('status')}</th>
                        <th style={{ width: 'auto', whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="no-data">
                            Loading...
                          </td>
                        </tr>
                      ) : states.length > 0 ? (
                        states.map((state, index) => (
                          <tr
                            key={state.id}
                            draggable={canManageMasters}
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`draggable-row ${draggedItem === index ? 'dragging' : ''}`}
                          >
                            <td className="drag-handle">
                              <span className="drag-icon">⋮⋮</span>
                            </td>
                            <td>{state.state_english}</td>
                            <td>{state.state_hindi}</td>
                            <td>{state.sequence || '-'}</td>
                            <td>
                              <span className={`badge ${state.is_active ? 'active' : 'inactive'}`}>
                                {state.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              {canManageMasters && (
                                <button
                                  className="btn-small btn-edit"
                                  onClick={() => handleEdit(state)}
                                >
                                  Edit
                                </button>
                              )}
                              {canDeleteMasters && (
                                <button
                                  className="btn-small btn-delete"
                                  onClick={() => confirmDelete(state.id)}
                                >
                                  Delete
                                </button>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="no-data">
                            No states found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <StateForm
                stateId={editingId}
                onClose={handleFormClose}
                onSuccess={(msg) => setMessage(msg)}
              />
            )}

            <ConfirmDialog
              open={confirm.open}
              title={confirm.title}
              message={confirm.message}
              onCancel={() => setConfirm({ ...confirm, open: false })}
              onConfirm={handleDeleteConfirmed}
              confirmLabel="Delete"
              cancelLabel="Cancel"
            />
          </div>
        </main>
      </div>
    </div>
  );
}
